import { OrgAvatar, SkeletonList, brandTokens, isolate } from '@batiflow/ui';
import { __css, mergeClasses, tokens } from '@fluentui/react-components';
import { PeopleTeam20Regular } from '@fluentui/react-icons';
import { useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { AppLink } from '@/shared/components/app-link';
import {
  PaneFooterArrow,
  PaneRowContent,
  PaneSection,
  usePaneNavStyles,
} from '@/shared/components/pane-nav';
import { useMySpaces } from './api';
import type { MySpace } from './model';

/** The list pane shows at most this many spaces; the rest is behind "all shared spaces". */
const PANE_LIMIT = 10;

const useStyles = __css({"tile":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","Brf1p80":"f4d9j23","Bns2gvi":"f1oa9isr","st59p4":"f1da94u2","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","De3pzq":"fw3pusv","sj55zd":"f19n0e5"},"external":{"Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f1nqfz0o","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"f44lkw9","De3pzq":"f1ynw2h5","sj55zd":"fhc94cr","Bhrd7zp":"fl43uef","Huce71":"fz5stix"}});

/** The spaces worth a line of their own: the project spaces are already in the projects list. */
const isListed = (space: MySpace): boolean =>
  space.external || space.kind === 'shared' || space.kind === 'group';

/**
 * "Espaces partagés" of the Projects list pane (COL-FE-01): the shared spaces of the person's
 * organisations and the external ones, each opening /s/{id}. An external space carries its host
 * organisation's avatar, an "External" mark and its host's name, so it is never taken for one of
 * the person's own.
 */
export function SharedSpacesSection() {
  const { t } = useTranslation('spaces');
  const styles = useStyles();
  const nav = usePaneNavStyles();
  const spaces = useMySpaces();
  const { spaceId } = useParams({ strict: false });
  const listed = (spaces.data?.items ?? []).filter(isListed);
  const items = listed.slice(0, PANE_LIMIT);

  return (
    <PaneSection
      title={t('listPane.title')}
      {...(spaces.data
        ? { count: listed.length, countLabel: t('listPane.count', { count: listed.length }) }
        : {})}
    >
      {spaces.isError ? (
        <p className={nav.note}>{t('listPane.loadFailed')}</p>
      ) : spaces.isPending ? (
        <SkeletonList count={2} density="compact" />
      ) : items.length === 0 ? (
        <p className={nav.note}>{t('listPane.empty')}</p>
      ) : (
        <ul className={nav.list} aria-label={t('listPane.label')}>
          {items.map((space) => {
            const current = space.id === spaceId;
            return (
              <li key={space.id}>
                <AppLink
                  to="/s/$spaceId"
                  params={{ spaceId: space.id }}
                  className={mergeClasses(nav.item, current && nav.active)}
                  title={space.name}
                  {...(current ? { 'aria-current': 'page' as const } : {})}
                >
                  <PaneRowContent
                    active={current}
                    tile={
                      space.external ? (
                        <OrgAvatar
                          name={space.host.name}
                          size={32}
                          decorative
                          {...(space.host.logoUrl ? { logoUrl: space.host.logoUrl } : {})}
                        />
                      ) : (
                        <span className={styles.tile} aria-hidden="true">
                          <PeopleTeam20Regular />
                        </span>
                      )
                    }
                    name={<bdi>{space.name}</bdi>}
                    meta={
                      space.external ? (
                        <>
                          <span className={styles.external}>{t('listPane.external')}</span>
                          <span>{t('listPane.hostedBy', { host: isolate(space.host.name) })}</span>
                        </>
                      ) : null
                    }
                  />
                </AppLink>
              </li>
            );
          })}
        </ul>
      )}
      <AppLink to="/spaces" className={nav.footer}>
        {t('listPane.seeAll')}
        <PaneFooterArrow />
      </AppLink>
    </PaneSection>
  );
}


import "/opt/batiflow/app/apps/web/src/features/spaces/shared-spaces-section.griffel.css";